import { decode } from 'base64-arraybuffer';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';

import { supabase } from '@/lib/supabase';

export type IdentityVerification = {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  front_id_path: string;
  back_id_path: string;
  selfie_path: string;
  bill_path: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
};

export async function captureIdPhoto(
  facing: 'front' | 'back' = 'back',
  purpose: 'id' | 'selfie' | 'bill' = 'id',
): Promise<{
  uri: string;
  base64: string;
  ext: string;
  mimeType: string;
} | null> {
  const perm = await ImagePicker.requestCameraPermissionsAsync();
  if (!perm.granted) throw new Error('Χρειάζεται άδεια πρόσβασης στην κάμερα.');

  // Πλαίσιο-οδηγός με native crop πριν καν φύγει η φωτογραφία από τη συσκευή:
  // αναλογία κάρτας (ID-1, ~1.586:1) για ταυτότητα/λογαριασμό, ελεύθερη
  // (χωρίς fixed aspect) για selfie ώστε να μη «στριμώχνεται» το πρόσωπο.
  const result = await ImagePicker.launchCameraAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    quality: 1,
    cameraType: facing === 'front'
      ? ImagePicker.CameraType.front
      : ImagePicker.CameraType.back,
    allowsEditing: true,
    ...(purpose !== 'selfie' ? { aspect: [159, 100] as [number, number] } : {}),
  });

  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;

  // ΜΟΝΟ width — το expo-image-manipulator διατηρεί την αναλογία διαστάσεων
  // όταν δίνεται μία μόνο πλευρά. Με width+height μαζί (όπως πριν) η εικόνα
  // «τεντωνόταν» σε τετράγωνο, παραμορφώνοντας την ταυτότητα.
  const compressed = await ImageManipulator.manipulateAsync(
    asset.uri,
    [{ resize: { width: 1600 } }],
    { compress: 0.85, format: ImageManipulator.SaveFormat.JPEG, base64: true },
  );

  return { uri: compressed.uri, base64: compressed.base64!, ext: 'jpg', mimeType: 'image/jpeg' };
}

export async function uploadIdPhoto(
  userId: string,
  label: 'front' | 'back' | 'selfie' | 'bill',
  base64: string,
  ext: string,
  mimeType: string,
): Promise<string> {
  const path = `${userId}/id-${label}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('verifications')
    .upload(path, decode(base64), { contentType: mimeType, upsert: true });
  if (error) {
    console.error('[uploadIdPhoto]', label, error);
    throw error;
  }
  return path;
}

export async function submitIdentityVerification(
  userId: string,
  frontPath: string,
  backPath: string,
  selfiePath: string,
  billPath?: string,
): Promise<IdentityVerification> {
  const { data, error } = await supabase
    .from('identity_verifications')
    .insert({ user_id: userId, front_id_path: frontPath, back_id_path: backPath, selfie_path: selfiePath, bill_path: billPath ?? null })
    .select('*')
    .single();
  if (error) throw error;
  return data as IdentityVerification;
}

export async function getMyIdentityVerification(userId: string): Promise<IdentityVerification | null> {
  const { data, error } = await supabase
    .from('identity_verifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as IdentityVerification | null;
}

export async function listPendingIdentityVerifications(): Promise<IdentityVerification[]> {
  const { data, error } = await supabase
    .from('identity_verifications')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as IdentityVerification[];
}

/** Ελαφρύ count (χωρίς δεδομένα) — για badge ένδειξη εκκρεμών επαληθεύσεων ταυτότητας. */
export async function countPendingIdentityVerifications(): Promise<number> {
  const { count, error } = await supabase
    .from('identity_verifications')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'pending');
  if (error) throw error;
  return count ?? 0;
}

export async function getIdentityPhotoUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('verifications')
    .createSignedUrl(path, 120);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * Οριστική διαγραφή των αρχείων ταυτοποίησης από το Storage (GDPR cleanup).
 * Πρέπει να καλείται πάντα μετά από reviewIdentityVerification, είτε σε
 * έγκριση είτε σε απόρριψη — δεν κρατάμε ευαίσθητα έγγραφα μετά τον έλεγχο.
 */
export async function deleteIdentityDocuments(req: Pick<IdentityVerification, 'front_id_path' | 'back_id_path' | 'selfie_path' | 'bill_path'>): Promise<void> {
  const paths = [req.front_id_path, req.back_id_path, req.selfie_path, req.bill_path].filter((p): p is string => !!p);
  if (paths.length === 0) return;
  const { error } = await supabase.storage.from('verifications').remove(paths);
  if (error) {
    console.error('[deleteIdentityDocuments]', error);
    throw error;
  }
}

export async function reviewIdentityVerification(
  id: string,
  approve: boolean,
  rejectionReason?: string,
  correctedName?: string,
): Promise<void> {
  const { error } = await supabase.rpc('review_identity_verification', {
    p_id: id,
    p_approve: approve,
    p_rejection_reason: rejectionReason,
    p_corrected_name: correctedName,
  });
  if (error) throw error;
}

export type VerificationAuditEntry = {
  id: string;
  user_id: string;
  user_name: string | null;
  kind: 'phone' | 'identity';
  action: 'approved' | 'rejected' | 'manual_grant' | 'manual_revoke';
  reviewed_by: string | null;
  reviewer_name: string | null;
  note: string | null;
  created_at: string;
};

/** Ιστορικό όλων των αλλαγών επαλήθευσης (κινητό/ταυτότητα) — admin only. */
export async function getVerificationAuditLog(limit = 100): Promise<VerificationAuditEntry[]> {
  const { data, error } = await supabase.rpc('get_verification_audit_log' as any, { p_limit: limit });
  if (error) throw error;
  return ((data ?? []) as unknown) as VerificationAuditEntry[];
}
